import { GIFTS, type GiftOption } from '@/constants';
import type { GiftCategory } from '@/types';

interface GiftSelectorProps {
  value: GiftCategory | null;
  onChange: (value: GiftCategory | null) => void;
  error?: string;
  required?: boolean;
}

export function GiftSelector({
  value,
  onChange,
  error,
  required = false,
}: GiftSelectorProps) {
  return (
    <div className="w-full">
      <label className="block text-sm font-medium text-gray-700 mb-2">
        Regalo entregado
        {required && <span className="text-danger-500 ml-1">*</span>}
      </label>

      <div className="grid grid-cols-2 gap-3">
        {GIFTS.map((gift: GiftOption) => (
          <GiftCard
            key={gift.id}
            gift={gift}
            isSelected={value === gift.id}
            onClick={() => onChange(value === gift.id ? null : gift.id)}
          />
        ))}
      </div>

      {error && (
        <p className="mt-2 text-sm text-danger-500" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}

interface GiftCardProps {
  gift: GiftOption;
  isSelected: boolean;
  onClick: () => void;
}

function GiftCard({ gift, isSelected, onClick }: GiftCardProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`
        relative flex w-full flex-col overflow-hidden rounded-lg border-2
        text-left transition-all duration-200
        ${
          isSelected
            ? 'border-primary-500'
            : 'border-gray-200 hover:border-gray-300'
        }
      `}
      role="radio"
      aria-checked={isSelected}
    >
      <div className="h-32 w-full bg-gray-50">
        <img
          src={gift.image}
          alt={gift.name}
          loading="lazy"
          className="h-full w-full object-contain"
        />
      </div>

      <div className={`px-3 py-2 ${isSelected ? 'bg-primary-50' : 'bg-white'}`}>
        <span
          className={`text-sm font-medium ${
            isSelected ? 'text-primary-700' : 'text-gray-700'
          }`}
        >
          {gift.name}
        </span>
      </div>

      {isSelected && (
        <span className="absolute right-2 top-2 flex h-6 w-6 items-center justify-center rounded-full bg-primary-500 text-white shadow">
          <svg
            className="h-4 w-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={3}
              d="M5 13l4 4L19 7"
            />
          </svg>
        </span>
      )}
    </button>
  );
}
